import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Me from '../../Images/Me.jpeg';
import Marquee from '../ui/Marquee';
import AntButton from '../ui/AntButton';
import HeroStats from './HeroStats';
import { heroTerminal, marqueeItems, personalInfo } from '../../data/portfolioData';
import { floatOrb, heroStagger, fadeUp } from '../../motion/variants';
import { scrollToSection } from '../../utils/scrollTo';

const HomeHero = () => {
  const [typedStatus, setTypedStatus] = useState('');
  const [showCaret, setShowCaret] = useState(true);
  const statusText = personalInfo.hero.status;
  const doubledLogs = [...heroTerminal.logs, ...heroTerminal.logs];

  useEffect(() => {
    let index = 0;
    const typeInterval = setInterval(() => {
      if (index <= statusText.length) {
        setTypedStatus(statusText.slice(0, index));
        index += 1;
      } else {
        clearInterval(typeInterval);
      }
    }, 28);
    return () => clearInterval(typeInterval);
  }, [statusText]);

  useEffect(() => {
    const blink = setInterval(() => setShowCaret((v) => !v), 530);
    return () => clearInterval(blink);
  }, []);

  return (
    <div className="hero-section relative min-h-dvh overflow-hidden bg-ant-bg text-ant-fg">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,_rgba(201,168,76,0.1),_transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_60%,_rgba(255,255,255,0.04),_transparent_40%)]" />
        <motion.div
          className="absolute top-[18%] left-[8%] w-64 h-64 rounded-full bg-ant-gold/[0.04] blur-3xl"
          {...floatOrb(0)}
        />
        <motion.div
          className="absolute bottom-[22%] right-[6%] w-80 h-80 rounded-full bg-ant-green/[0.03] blur-3xl"
          {...floatOrb(1.5)}
        />
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
      </div>

      <div className="relative z-10 flex flex-col min-h-dvh pt-24 lg:pt-28">
        <div className="flex-1 flex items-center">
          <div className="mx-auto max-w-screen-xl px-4 sm:px-6 lg:px-8 w-full py-12 lg:py-20">
            <div className="grid lg:grid-cols-12 gap-8 sm:gap-12 lg:gap-16 items-center">
              <motion.div
                className="lg:col-span-7 text-center lg:text-left"
                initial="hidden"
                animate="visible"
                variants={heroStagger}
              >
                <motion.p
                  variants={fadeUp}
                  className="font-mono text-[11px] tracking-[0.3em] uppercase text-ant-gold/80 mb-8"
                >
                  {personalInfo.hero.eyebrow} · {personalInfo.location.split(',')[0]}
                </motion.p>

                <motion.h1
                  variants={fadeUp}
                  className="text-[2rem] xs:text-[2.75rem] sm:text-5xl lg:text-6xl xl:text-[4.25rem] font-semibold tracking-[-0.03em] leading-[1.08] mb-6 sm:mb-8"
                >
                  {personalInfo.hero.line1}
                  <br />
                  <span className="text-white/90 ant-glow-text">{personalInfo.hero.line2}</span>
                </motion.h1>

                <motion.p
                  variants={fadeUp}
                  className="text-lg sm:text-xl text-white/45 max-w-xl mx-auto lg:mx-0 leading-relaxed mb-10"
                >
                  {personalInfo.tagline}
                </motion.p>

                <motion.div
                  variants={fadeUp}
                  className="flex flex-col xs:flex-row flex-wrap gap-3 sm:gap-4 justify-center lg:justify-start mb-10 sm:mb-12"
                >
                  <AntButton onClick={() => scrollToSection('contact')} className="w-full xs:w-auto">
                    {personalInfo.hero.ctaPrimary}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </AntButton>
                  <AntButton
                    variant="secondary"
                    onClick={() => scrollToSection('experience')}
                    className="w-full xs:w-auto"
                  >
                    {personalInfo.hero.ctaSecondary}
                  </AntButton>
                </motion.div>

                <motion.div variants={fadeUp}>
                  <HeroStats />
                </motion.div>
              </motion.div>

              <motion.div
                className="lg:col-span-5"
                initial={{ opacity: 0, y: 40, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.85, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <motion.div
                  className="ant-card ant-ring relative rounded-2xl border border-white/[0.08] bg-white/[0.02] p-1 overflow-hidden"
                  whileHover={{ y: -4 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="rounded-xl border border-white/[0.06] bg-ant-bg-2 overflow-hidden">
                    <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] bg-white/[0.02]">
                      <span className="w-2.5 h-2.5 rounded-full bg-white/10" />
                      <span className="w-2.5 h-2.5 rounded-full bg-white/10" />
                      <span className="w-2.5 h-2.5 rounded-full bg-ant-gold/60" />
                      <span className="ml-2 font-mono text-[10px] tracking-widest text-white/25">
                        {heroTerminal.windowLabel}
                      </span>
                    </div>

                    <div className="p-5 space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-full overflow-hidden border border-white/10 shrink-0">
                          <img src={Me} alt={personalInfo.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 rounded-xl border border-white/[0.06] bg-white/[0.03] p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="ant-dot" />
                            <p className="font-mono text-[10px] text-ant-gold/70">{heroTerminal.statusCmd}</p>
                          </div>
                          <p className="text-sm text-white/80 leading-relaxed min-h-[3.5rem]">
                            {typedStatus}
                            {showCaret && <span className="ant-caret ml-0.5" />}
                          </p>
                        </div>
                      </div>

                      <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 font-mono text-xs h-[108px] overflow-hidden">
                        <div className="terminal-scroll space-y-2">
                          {doubledLogs.map((log, i) => (
                            <p
                              key={`${log.text}-${i}`}
                              className={log.type === 'ok' ? 'text-ant-green' : 'text-white/25'}
                            >
                              {log.text}
                            </p>
                          ))}
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {heroTerminal.stack.map((tech) => (
                          <span
                            key={tech}
                            className="px-3 py-1 rounded-full border border-white/[0.08] bg-white/[0.03] text-[11px] font-mono text-white/40"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>

                      <p className="font-mono text-xs text-white/40">
                        <span className="text-white/60">{personalInfo.name}</span>
                        <span className="ant-caret ml-0.5" />
                      </p>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>

        <Marquee items={marqueeItems} />
      </div>
    </div>
  );
};

export default HomeHero;